import { Column } from '@tanstack/react-table';
import { Table as ITable } from '@tanstack/table-core';
import {
    faArrowDown91,
    faArrowDownWideShort,
    faArrowDownZA,
    faArrowUp19,
    faArrowUpAZ,
    faArrowUpShortWide,
} from '@fortawesome/pro-regular-svg-icons';
import { TableIgnoredColumns } from 'components/table/TableConstants';
import { twJoin } from 'tailwind-merge';
import { ReportResource } from 'types/domain';
import { FilterCacheKey } from 'types/filter-cache-key';
import { ColumnSortType } from 'types/react-table';

const getComputedColumnProps = <T>(table: ITable<T>, column: Column<T>) => {
    const pinnedColumns = table.getLeftVisibleLeafColumns() ?? [];
    const index = pinnedColumns.findIndex(c => c.id === column.id);
    const isLastPinned = index === pinnedColumns.length - 1;

    const isOrphanedAction = column
        ? pinnedColumns.length === 1 && TableIgnoredColumns.includes(column.id)
        : false;

    const pinnedClassName = twJoin(
        !isOrphanedAction && isStickyColumn(column) && 'md:sticky z-layerTen',
        !isOrphanedAction &&
            isStickyColumn(column) &&
            isLastPinned &&
            'after:absolute after:z-pentOutermost after:-right-1 after:bg-gray-200 after:w-1 after:-top-1 after:bottom-0',
    );

    return {
        isLastPinned,
        pinnedClassName,
    };
};

const isStickyColumn = <T>(column: Column<T>) => column.getIsPinned() === 'left';

const ascIconFactory = (type: ColumnSortType) => {
    switch (type) {
        case 'alpha':
            return faArrowUpAZ;
        case 'numeric':
            return faArrowUp19;
        case 'date':
            return faArrowUpShortWide;
    }
};

const descIconFactory = (type: ColumnSortType) => {
    switch (type) {
        case 'alpha':
            return faArrowDownZA;
        case 'numeric':
            return faArrowDown91;
        case 'date':
            return faArrowDownWideShort;
    }
};

const isTableEmpty = <T>(table: ITable<T>) => {
    return table.options.data.length === 0;
};

const getTableResourceFromReportResource = (resource: ReportResource): FilterCacheKey => {
    if (resource === 'transaction_plans') return 'plans';
    return resource;
};

const getReadableSingleResourceFromReportResource = (resource: ReportResource): string => {
    switch (resource) {
        case 'contacts':
            return 'contact';
        case 'companies':
            return 'company';
        case 'transactions':
            return 'transaction';
        case 'tickets':
            return 'ticket';
        case 'campaigns':
            return 'campaign';
        case 'transaction_plans':
            return 'plan';
        case 'pledges':
            return 'pledge';
    }
};

export {
    getComputedColumnProps,
    isStickyColumn,
    isTableEmpty,
    ascIconFactory,
    descIconFactory,
    getTableResourceFromReportResource,
    getReadableSingleResourceFromReportResource,
};
